/**
 * =============================================================================
 * IDS CRM Web - Form designer (Workscope forms Phase 1, step 6; Gareth 2026-10-22)
 * =============================================================================
 *
 * The draft editor's Build view, in three columns:
 *  - Outline: sections and questions in order. Pick one to edit it; add
 *    sections and questions; move, archive, restore, delete.
 *  - Editor: everything about the picked question (wording, help, required,
 *    its type's settings, show-when, evidence, what a fail demands) or the
 *    picked section (its title).
 *  - Try it: the form as an engineer sees it, filled in here and checked with
 *    the same rules the screens and server use. Nothing is saved.
 *
 * Gareth's standing concern is that the designer must not lose the freedom to
 * build what is needed: everything the old editor could do is here (labels,
 * required on save and on complete, photos, pass and fail values, follow-ups,
 * archive and restore, duplicate, move, add sections and questions, the
 * completion tick's wording), with system fields locked as before.
 *
 * Props: schema, onChange(nextSchema), publishedKeys ({ sections, fields }
 * Sets, or null when not known: then nothing is deletable).
 * =============================================================================
 */

import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import TemplateCheckFormRenderer, { validateAnswers } from './TemplateCheckFormRenderer';
import { filesToAttachmentPayload } from '../utils/attachments';
import {
  QUESTION_TYPES,
  TYPE_LABELS,
  FAILABLE_TYPES,
  ANSWERLESS_TYPES,
  allQuestionKeys,
  keyFromLabel,
  sortedSections,
  sortedFields,
  findQuestion,
  newQuestion,
  addSection,
  addQuestion,
  setQuestionSetting,
  updateSection,
  updateFollowUpField,
  setFollowUpCondition,
  removeFollowUp,
  moveQuestion,
  moveQuestionToSection,
  moveSection,
  toggleArchiveQuestion,
  toggleArchiveSection,
  deleteQuestion,
  deleteSection,
  duplicateQuestion,
  questionsBefore,
  dependentsOf,
  optionValueFromLabel,
  isSystemQuestion,
  isCompletionTick,
  sectionHasSystemFields
} from '../utils/formSchemaOps';
import './FormDesigner.css';

const YES_NO_VALUES = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
  { value: 'na', label: 'N/A' }
];

const SHOW_WHEN_TESTS = [
  { value: 'answered', label: 'is answered' },
  { value: 'any_of', label: 'is one of' },
  { value: 'fails', label: 'fails' },
  { value: 'passes', label: 'passes' }
];

const answerValuesOf = (field) => {
  if (!field) return [];
  if (field.field_type === 'yes_no') return YES_NO_VALUES.filter(item => item.value !== 'na');
  if (field.field_type === 'yes_no_na') return YES_NO_VALUES;
  if (field.field_type === 'choice' || field.field_type === 'multi_choice') {
    return (field.options || []).map(option => ({ value: option.value, label: option.label }));
  }
  return [];
};

const numberOrNull = (text) => {
  const trimmed = String(text ?? '').trim();
  if (trimmed === '') return null;
  const value = Number(trimmed.replace(',', '.'));
  return Number.isFinite(value) ? value : trimmed;
};

function FormDesigner({ schema, onChange, publishedKeys = null }) {
  const [selected, setSelected] = useState(() => {
    const first = sortedSections(schema)[0];
    const firstField = first ? sortedFields(first)[0] : null;
    return firstField ? { kind: 'question', key: firstField.field_key } : (first ? { kind: 'section', key: first.section_key } : null);
  });
  const [adding, setAdding] = useState(null); // { sectionKey, label, type, error }
  const [notice, setNotice] = useState(null);
  const [newOption, setNewOption] = useState('');
  const [preview, setPreview] = useState({ answers: {}, attachments: {}, errors: null });

  const isPublishedField = (fieldKey) => Boolean(publishedKeys && publishedKeys.fields.has(fieldKey));
  const fieldIsDeletable = (field) => Boolean(publishedKeys && field && !isSystemQuestion(field)
    && !publishedKeys.fields.has(field.field_key)
    && !(field.follow_up?.field?.field_key && publishedKeys.fields.has(field.follow_up.field.field_key)));
  const sectionIsDeletable = (section) => Boolean(publishedKeys && section
    && !publishedKeys.sections.has(section.section_key)
    && (section.fields || []).every(fieldIsDeletable));

  const change = (next, message = null) => {
    onChange(next);
    setNotice(message);
  };

  // A question others depend on loses them their condition when it goes, so
  // they show always rather than never, and the builder is told.
  const releaseDependents = (next, fieldKey) => {
    const dependents = dependentsOf(next, fieldKey);
    let updated = next;
    dependents.forEach(field => { updated = setQuestionSetting(updated, field.field_key, 'show_when', null); });
    return { schema: updated, released: dependents };
  };

  const dependentsMessage = (released) => (released.length === 0 ? null
    : `${released.map(field => `"${field.label}"`).join(', ')} depended on it and will now always show.`);

  // New wording. A question no published version has carried, that no other
  // question depends on, takes a key from its new wording, so a question
  // added as "What is wrong?" and reworded is not left keyed what_is_wrong.
  // Published ones keep their key: filled-in forms use it.
  const relabel = (field, label) => {
    let next = setQuestionSetting(schema, field.field_key, 'label', label);
    const free = publishedKeys && !publishedKeys.fields.has(field.field_key) && !isSystemQuestion(field)
      && dependentsOf(schema, field.field_key).length === 0 && label.trim();
    if (free) {
      const others = allQuestionKeys(schema);
      others.delete(field.field_key);
      const newKey = keyFromLabel(label, others);
      if (newKey !== field.field_key) {
        next = setQuestionSetting(next, field.field_key, 'field_key', newKey);
        setSelected({ kind: 'question', key: newKey });
      }
    }
    change(next);
  };

  const selectedQuestion = selected?.kind === 'question' ? findQuestion(schema, selected.key) : null;
  const selectedSection = selected?.kind === 'section'
    ? (schema.sections || []).find(section => section.section_key === selected.key)
    : null;

  // ---------------------------------------------------------------- adding
  const startAdding = (sectionKey) => setAdding({ sectionKey, label: '', type: 'yes_no', error: '' });

  const finishAdding = () => {
    const label = adding.label.trim();
    if (!label) {
      setAdding(prev => ({ ...prev, error: 'Write the question first.' }));
      return;
    }
    const field = newQuestion(adding.type, label, allQuestionKeys(schema));
    const afterKey = selectedQuestion && selectedQuestion.section.section_key === adding.sectionKey ? selectedQuestion.field.field_key : null;
    change(addQuestion(schema, adding.sectionKey, field, afterKey));
    setSelected({ kind: 'question', key: field.field_key });
    setAdding(null);
  };

  const handleAddSection = () => {
    const { schema: next, sectionKey } = addSection(schema);
    change(next);
    setSelected({ kind: 'section', key: sectionKey });
  };

  // A follow-up is an ordinary question shown when this one fails (or on
  // the answer picked), placed straight after it.
  const addFollowUpQuestion = (field, sectionKey) => {
    const label = 'What is wrong?';
    const follow = newQuestion('text', label, allQuestionKeys(schema));
    follow.show_when = FAILABLE_TYPES.has(field.field_type)
      ? { field_key: field.field_key, is: 'fails' }
      : { field_key: field.field_key, is: 'answered' };
    change(addQuestion(schema, sectionKey, follow, field.field_key), 'Follow-up added. Change its wording and when it shows below.');
    setSelected({ kind: 'question', key: follow.field_key });
  };

  // ---------------------------------------------------------------- outline
  const renderOutline = () => (
    <div className="fd-outline" aria-label="Form outline">
      <div className="fd-panel-head">
        <h4>Outline</h4>
        <button type="button" className="btn-secondary btn-small" onClick={handleAddSection}>+ Add Section</button>
      </div>
      {sortedSections(schema).length === 0 && (
        <p className="fd-empty">No sections yet. Add a section, then its questions.</p>
      )}
      {sortedSections(schema).map((section, sectionIndex, sections) => (
        <div key={section.section_key} className={`fd-outline-section ${section.archived ? 'archived' : ''}`}>
          <div className={`fd-outline-section-row ${selected?.kind === 'section' && selected.key === section.section_key ? 'selected' : ''}`}>
            <button
              type="button"
              className="fd-outline-pick fd-outline-section-title"
              onClick={() => setSelected({ kind: 'section', key: section.section_key })}
            >
              {section.title || <em>Untitled section</em>}
              {section.archived && <span className="archived-badge">Archived</span>}
            </button>
            <div className="fd-row-actions">
              <button type="button" className="btn-icon" title="Move section up" aria-label={`Move section ${section.title} up`}
                disabled={sectionIndex === 0} onClick={() => change(moveSection(schema, section.section_key, -1))}>↑</button>
              <button type="button" className="btn-icon" title="Move section down" aria-label={`Move section ${section.title} down`}
                disabled={sectionIndex === sections.length - 1} onClick={() => change(moveSection(schema, section.section_key, 1))}>↓</button>
            </div>
          </div>
          {!section.archived && (
            <ul className="fd-outline-questions">
              {sortedFields(section).map(field => (
                <li key={field.field_key}>
                  <button
                    type="button"
                    className={`fd-outline-pick fd-outline-question ${selected?.kind === 'question' && selected.key === field.field_key ? 'selected' : ''} ${field.archived ? 'archived' : ''} ${field.show_when ? 'dependent' : ''}`}
                    onClick={() => setSelected({ kind: 'question', key: field.field_key })}
                  >
                    <span className="fd-outline-label">{field.label || <em>Untitled question</em>}</span>
                    <span className="fd-outline-meta">
                      {TYPE_LABELS[field.field_type] || field.field_type}
                      {field.required_on_complete ? ' · Required' : ''}
                      {field.show_when ? ' · Shows when…' : ''}
                      {field.archived ? ' · Archived' : ''}
                      {isSystemQuestion(field) ? ' · System' : ''}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {!section.archived && (
            adding?.sectionKey === section.section_key ? renderAddQuestion() : (
              <button type="button" className="fd-add-question" onClick={() => startAdding(section.section_key)}>+ Add Question</button>
            )
          )}
        </div>
      ))}
    </div>
  );

  const renderAddQuestion = () => (
    <div className="fd-add-panel" role="group" aria-label="Add a question">
      <label htmlFor="fd-new-question">Question</label>
      <input
        id="fd-new-question"
        type="text"
        value={adding.label}
        autoFocus
        onChange={(e) => setAdding(prev => ({ ...prev, label: e.target.value, error: '' }))}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); finishAdding(); } }}
        placeholder="e.g. Is the cell guarding secure?"
      />
      <span className="fd-label">Answer type</span>
      <div className="fd-type-grid" role="radiogroup" aria-label="Answer type">
        {QUESTION_TYPES.map(type => (
          <label key={type.value} className={`fd-type ${adding.type === type.value ? 'checked' : ''}`} title={type.hint}>
            <input
              type="radio"
              name="fd-new-question-type"
              aria-label={type.label}
              value={type.value}
              checked={adding.type === type.value}
              onChange={() => setAdding(prev => ({ ...prev, type: type.value }))}
            />
            <span className="fd-type-name">{type.label}</span>
            <span className="fd-type-hint">{type.hint}</span>
          </label>
        ))}
      </div>
      {adding.error && <p className="fd-error" role="alert">{adding.error}</p>}
      <div className="fd-add-actions">
        <button type="button" className="btn-secondary btn-small" onClick={() => setAdding(null)}>Cancel</button>
        <button type="button" className="btn-primary btn-small" onClick={finishAdding}>Add Question</button>
      </div>
    </div>
  );

  // ---------------------------------------------------------------- question editor
  const renderShowWhen = (field) => {
    const before = questionsBefore(schema, field.field_key);
    const rule = field.show_when || null;
    const parent = rule ? before.find(item => item.field_key === rule.field_key) || findQuestion(schema, rule.field_key)?.field : null;
    const values = answerValuesOf(parent);
    const tests = SHOW_WHEN_TESTS.filter(test => (test.value === 'fails' || test.value === 'passes') ? FAILABLE_TYPES.has(parent?.field_type)
      : test.value === 'any_of' ? values.length > 0 : true);
    const setRule = (next) => change(setQuestionSetting(schema, field.field_key, 'show_when', next));

    return (
      <fieldset className="fd-fieldset">
        <legend>When it shows</legend>
        <div className="fd-inline">
          <select
            aria-label="Show this question"
            value={rule ? rule.field_key : ''}
            onChange={(e) => setRule(e.target.value ? { field_key: e.target.value, is: 'answered' } : null)}
          >
            <option value="">Always</option>
            {before.map(item => <option key={item.field_key} value={item.field_key}>When "{item.label}"…</option>)}
          </select>
          {rule && (
            <select aria-label="Condition" value={rule.is} onChange={(e) => setRule({ field_key: rule.field_key, is: e.target.value, ...(e.target.value === 'any_of' ? { values: [] } : {}) })}>
              {tests.map(test => <option key={test.value} value={test.value}>{test.label}</option>)}
            </select>
          )}
        </div>
        {rule?.is === 'any_of' && (
          <div className="fd-checks" role="group" aria-label="Answers that show it">
            {values.map(item => (
              <label key={item.value} className="toggle-label">
                <input
                  type="checkbox"
                  checked={(rule.values || []).map(String).includes(String(item.value))}
                  onChange={(e) => setRule({
                    ...rule,
                    values: e.target.checked
                      ? [...(rule.values || []), item.value]
                      : (rule.values || []).filter(value => String(value) !== String(item.value))
                  })}
                />
                <span>{item.label}</span>
              </label>
            ))}
          </div>
        )}
        {before.length === 0 && <p className="fd-hint">Only questions earlier in the form can decide when this one shows.</p>}
      </fieldset>
    );
  };

  const renderOptions = (field) => {
    const published = isPublishedField(field.field_key);
    const options = field.options || [];
    const setOptions = (next) => change(setQuestionSetting(schema, field.field_key, 'options', next));
    const addOption = () => {
      const label = newOption.trim();
      if (!label) return;
      setOptions([...options, { value: optionValueFromLabel(label, options), label }]);
      setNewOption('');
    };
    return (
      <fieldset className="fd-fieldset">
        <legend>Options</legend>
        {options.length === 0 && <p className="fd-hint">Add at least two options.</p>}
        <ul className="fd-options">
          {options.map((option, index) => (
            <li key={option.value}>
              <input
                type="text"
                aria-label={`Option ${index + 1}`}
                value={option.label}
                onChange={(e) => setOptions(options.map(item => (item.value === option.value ? { ...item, label: e.target.value } : item)))}
              />
              <label className="toggle-label" title="Choosing this counts as a fail">
                <input
                  type="checkbox"
                  checked={Boolean(option.fails)}
                  onChange={(e) => setOptions(options.map(item => (item.value === option.value ? { ...item, fails: e.target.checked } : item)))}
                />
                <span>Fails</span>
              </label>
              <button
                type="button"
                className="btn-icon"
                aria-label={`Remove option ${option.label}`}
                title={published ? 'A published question keeps its options; change the wording instead' : 'Remove option'}
                disabled={published}
                onClick={() => setOptions(options.filter(item => item.value !== option.value))}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
        <div className="fd-inline">
          <input
            type="text"
            aria-label="New option"
            value={newOption}
            placeholder="New option"
            onChange={(e) => setNewOption(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOption(); } }}
          />
          <button type="button" className="btn-secondary btn-small" onClick={addOption}>Add option</button>
        </div>
      </fieldset>
    );
  };

  const renderQuestionEditor = ({ field, section }) => {
    const system = isSystemQuestion(field);
    const completion = isCompletionTick(field);
    const locked = system && !completion;
    const failable = FAILABLE_TYPES.has(field.field_type);
    const answerless = ANSWERLESS_TYPES.has(field.field_type);
    const published = isPublishedField(field.field_key);
    const fields = sortedFields(section);
    const index = fields.findIndex(item => item.field_key === field.field_key);
    const set = (name, value) => change(setQuestionSetting(schema, field.field_key, name, value));
    const evidence = field.evidence || (field.allow_photos ? 'optional' : 'none');

    return (
      <div className="fd-editor-body">
        <div className="fd-editor-head">
          <div className="fd-badges">
            <span className="field-type-badge">{TYPE_LABELS[field.field_type] || field.field_type}</span>
            <span className="field-key-badge" title="Question key (locked)">{field.field_key}</span>
            {system && <span className="system-badge">System</span>}
            {field.archived && <span className="archived-badge">Archived</span>}
          </div>
          <div className="fd-row-actions">
            <button type="button" className="btn-icon" title="Move question up" aria-label="Move question up" disabled={index <= 0}
              onClick={() => change(moveQuestion(schema, field.field_key, -1))}>↑</button>
            <button type="button" className="btn-icon" title="Move question down" aria-label="Move question down" disabled={index >= fields.length - 1}
              onClick={() => change(moveQuestion(schema, field.field_key, 1))}>↓</button>
            <button type="button" className="btn-text" title={system ? 'System fields cannot be duplicated' : 'Duplicate question'} aria-label="Duplicate question" disabled={system}
              onClick={() => {
                const { schema: next, fieldKey } = duplicateQuestion(schema, field.field_key);
                change(next);
                if (fieldKey) setSelected({ kind: 'question', key: fieldKey });
              }}>Duplicate</button>
            <button
              type="button"
              className="btn-icon"
              title={system ? 'System fields cannot be archived' : (field.archived ? 'Restore question' : 'Archive question')}
              aria-label={field.archived ? 'Restore question' : 'Archive question'}
              disabled={system}
              onClick={() => {
                if (field.archived) {
                  change(toggleArchiveQuestion(schema, field.field_key));
                  return;
                }
                const { schema: released, released: dependents } = releaseDependents(schema, field.field_key);
                change(toggleArchiveQuestion(released, field.field_key), dependentsMessage(dependents));
              }}
            >
              {field.archived ? 'Restore' : 'Archive'}
            </button>
            {fieldIsDeletable(field) && (
              <button
                type="button"
                className="btn-icon fd-danger"
                title="Delete question (never published, so nothing uses it)"
                aria-label={`Delete ${field.label || 'question'}`}
                onClick={() => {
                  const { schema: released, released: dependents } = releaseDependents(schema, field.field_key);
                  change(deleteQuestion(released, field.field_key), dependentsMessage(dependents));
                  setSelected({ kind: 'section', key: section.section_key });
                }}
              >
                Delete
              </button>
            )}
          </div>
        </div>

        {field.archived ? (
          <p className="archived-field-notice">Archived: hidden from new forms but kept for history. Restore it to change it.</p>
        ) : (
          <>
            <div className="form-group">
              <label htmlFor="fd-question-label">{completion ? 'Completion wording' : (field.field_type === 'instruction' ? 'Heading' : 'Question')}</label>
              <input
                id="fd-question-label"
                type="text"
                value={field.label || ''}
                disabled={locked}
                onChange={(e) => relabel(field, e.target.value)}
              />
              {completion && <span className="field-hint">Engineers tick this to confirm the form is complete. Word it for this form.</span>}
            </div>

            {field.field_type === 'instruction' && (
              <div className="form-group">
                <label htmlFor="fd-instruction-body">Text to read</label>
                <textarea id="fd-instruction-body" rows={4} value={field.body || ''} onChange={(e) => set('body', e.target.value)} />
              </div>
            )}

            {!locked && field.field_type !== 'instruction' && (
              <div className="form-group">
                <label htmlFor="fd-help-text">Help for the engineer <span className="fd-optional">(optional)</span></label>
                <input id="fd-help-text" type="text" value={field.help_text || ''} onChange={(e) => set('help_text', e.target.value || null)} />
              </div>
            )}

            {field.field_type !== 'instruction' && (
              <div className="fd-checks">
                <label className="toggle-label">
                  <input type="checkbox" checked={Boolean(field.required_on_complete)} disabled={locked}
                    onChange={(e) => set('required_on_complete', e.target.checked)} />
                  <span>Required to complete</span>
                </label>
                <label className="toggle-label">
                  <input type="checkbox" checked={Boolean(field.required_on_save)} disabled={locked}
                    onChange={(e) => set('required_on_save', e.target.checked)} />
                  <span>Required to save</span>
                </label>
              </div>
            )}

            {(field.field_type === 'yes_no' || field.field_type === 'yes_no_na') && !locked && (
              <div className="fd-inline">
                <div className="form-group small">
                  <label htmlFor="fd-pass-value">Pass value</label>
                  <select id="fd-pass-value" value={field.pass_value || ''} onChange={(e) => set('pass_value', e.target.value || null)}>
                    <option value="">None</option>
                    {answerValuesOf(field).map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
                  </select>
                </div>
                <div className="form-group small">
                  <label htmlFor="fd-fail-value">Fail value</label>
                  <select id="fd-fail-value" value={field.fail_value || ''} onChange={(e) => set('fail_value', e.target.value || null)}>
                    <option value="">None</option>
                    {answerValuesOf(field).map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
                  </select>
                </div>
              </div>
            )}

            {field.field_type === 'reading' && (
              <div className="fd-inline">
                <div className="form-group small">
                  <label htmlFor="fd-unit">Unit</label>
                  <input id="fd-unit" type="text" value={field.unit || ''} placeholder="e.g. bar" disabled={published}
                    title={published ? 'A published reading keeps its unit; a new unit is a new question' : undefined}
                    onChange={(e) => set('unit', e.target.value)} />
                </div>
                <div className="form-group small">
                  <label htmlFor="fd-min">Lowest acceptable</label>
                  <input id="fd-min" type="text" inputMode="decimal" value={field.min ?? ''} onChange={(e) => set('min', numberOrNull(e.target.value))} />
                </div>
                <div className="form-group small">
                  <label htmlFor="fd-max">Highest acceptable</label>
                  <input id="fd-max" type="text" inputMode="decimal" value={field.max ?? ''} onChange={(e) => set('max', numberOrNull(e.target.value))} />
                </div>
              </div>
            )}

            {(field.field_type === 'choice' || field.field_type === 'multi_choice') && renderOptions(field)}

            {(field.field_type === 'photo' || field.field_type === 'file') && (
              <div className="form-group small">
                <label htmlFor="fd-min-count">How many are needed (when required)</label>
                <input id="fd-min-count" type="number" min={1} max={20} value={field.min_count ?? 1}
                  onChange={(e) => set('min_count', Math.min(20, Math.max(1, parseInt(e.target.value, 10) || 1)))} />
              </div>
            )}

            {!locked && renderShowWhen(field)}

            {!locked && !answerless && field.field_type !== 'signature' && (
              <div className="form-group">
                <label htmlFor="fd-evidence">Photos</label>
                <select
                  id="fd-evidence"
                  value={evidence}
                  onChange={(e) => {
                    const next = setQuestionSetting(schema, field.field_key, 'allow_photos', null);
                    change(setQuestionSetting(next, field.field_key, 'evidence', e.target.value === 'none' ? null : e.target.value));
                  }}
                >
                  <option value="none">No photos</option>
                  <option value="optional">Photos allowed</option>
                  <option value="required">Photo required</option>
                  {failable && <option value="on_fail">Photo required when it fails</option>}
                </select>
              </div>
            )}

            {!locked && failable && (
              <fieldset className="fd-fieldset">
                <legend>When the answer fails</legend>
                <div className="fd-checks">
                  <label className="toggle-label">
                    <input type="checkbox" checked={Boolean(field.on_fail?.note)}
                      onChange={(e) => set('on_fail', { ...(field.on_fail || {}), note: e.target.checked })} />
                    <span>Ask what is wrong</span>
                  </label>
                  <label className="toggle-label">
                    <input type="checkbox" checked={Boolean(field.on_fail?.photo)}
                      onChange={(e) => set('on_fail', { ...(field.on_fail || {}), photo: e.target.checked })} />
                    <span>Ask for a photo</span>
                  </label>
                </div>
              </fieldset>
            )}

            {field.follow_up?.field && (
              <fieldset className="fd-fieldset">
                <legend>Follow-up (older style)</legend>
                <div className="fd-inline">
                  <label htmlFor="fd-follow-condition">When the answer is</label>
                  <select id="fd-follow-condition" value={field.follow_up.condition}
                    onChange={(e) => change(setFollowUpCondition(schema, field.field_key, e.target.value))}>
                    {answerValuesOf(field).map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
                  </select>
                  <button type="button" className="btn-secondary btn-small" onClick={() => change(removeFollowUp(schema, field.field_key))}>
                    Remove follow-up
                  </button>
                </div>
                <div className="form-group">
                  <label htmlFor="fd-follow-label">Follow-up question</label>
                  <input id="fd-follow-label" type="text" value={field.follow_up.field.label || ''}
                    onChange={(e) => change(updateFollowUpField(schema, field.field_key, { label: e.target.value }))} />
                </div>
                <label className="toggle-label">
                  <input type="checkbox" checked={Boolean(field.follow_up.field.required_on_complete)}
                    onChange={(e) => change(updateFollowUpField(schema, field.field_key, { required_on_complete: e.target.checked }))} />
                  <span>Required to complete</span>
                </label>
              </fieldset>
            )}

            {!locked && !answerless && !field.follow_up && (
              <button type="button" className="btn-add-follow-up" onClick={() => addFollowUpQuestion(field, section.section_key)}>
                + Add Follow-up Question
              </button>
            )}

            {(schema.sections || []).filter(item => !item.archived).length > 1 && (
              <div className="form-group small">
                <label htmlFor="fd-move-section">Section</label>
                <select id="fd-move-section" value={section.section_key}
                  onChange={(e) => change(moveQuestionToSection(schema, field.field_key, e.target.value))}>
                  {sortedSections(schema).filter(item => !item.archived).map(item => (
                    <option key={item.section_key} value={item.section_key}>{item.title || item.section_key}</option>
                  ))}
                </select>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const renderSectionEditor = (section) => {
    const hasSystem = sectionHasSystemFields(section);
    return (
      <div className="fd-editor-body">
        <div className="fd-editor-head">
          <div className="fd-badges">
            <span className="field-type-badge">Section</span>
            <span className="section-key-badge" title="Section key (locked)">{section.section_key}</span>
            {section.archived && <span className="archived-badge">Archived</span>}
            {hasSystem && <span className="section-system-badge">System</span>}
          </div>
          <div className="fd-row-actions">
            <button
              type="button"
              className="btn-icon"
              title={hasSystem && !section.archived ? 'Cannot archive: contains system fields' : (section.archived ? 'Restore section' : 'Archive section')}
              aria-label={section.archived ? 'Restore section' : 'Archive section'}
              disabled={hasSystem && !section.archived}
              onClick={() => change(toggleArchiveSection(schema, section.section_key))}
            >
              {section.archived ? 'Restore' : 'Archive'}
            </button>
            {sectionIsDeletable(section) && (
              <button type="button" className="btn-icon fd-danger" title="Delete section (never published, so nothing uses it)"
                aria-label={`Delete section ${section.title || ''}`.trim()}
                onClick={() => { change(deleteSection(schema, section.section_key)); setSelected(null); }}>
                Delete
              </button>
            )}
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="fd-section-title">Section title {!section.title?.trim() && <span className="error-text">*Required</span>}</label>
          <input
            id="fd-section-title"
            type="text"
            value={section.title || ''}
            disabled={section.archived}
            className={!section.title?.trim() ? 'input-error' : ''}
            onChange={(e) => change(updateSection(schema, section.section_key, { title: e.target.value }))}
          />
        </div>
        {section.archived
          ? <p className="archived-field-notice">This section is archived and will not appear in live forms. {(section.fields || []).length} question(s) hidden.</p>
          : <p className="fd-hint">{(section.fields || []).filter(field => !field.archived).length} live question(s). Add questions from the outline.</p>}
      </div>
    );
  };

  // ---------------------------------------------------------------- try it
  const previewSchema = useMemo(() => ({ ...schema }), [schema]);

  const checkPreview = () => {
    const result = validateAnswers(previewSchema, preview.answers, { requireComplete: true, attachments: preview.attachments });
    setPreview(prev => ({ ...prev, errors: result.errors }));
  };

  const addPreviewFiles = async (stepKey, files, input) => {
    try {
      const picked = await filesToAttachmentPayload(files);
      setPreview(prev => ({
        ...prev,
        attachments: { ...prev.attachments, [stepKey]: [...(prev.attachments[stepKey] || []), ...picked.map(item => ({ ...item, step_key: stepKey }))] }
      }));
    } catch (err) {
      setNotice(err.message || 'The file could not be added.');
    } finally {
      if (input) input.value = '';
    }
  };

  const renderPreview = () => (
    <div className="fd-preview" aria-label="Try it">
      <div className="fd-panel-head">
        <h4>Try it</h4>
        <div className="fd-row-actions">
          <button type="button" className="btn-secondary btn-small" onClick={checkPreview}>Check as complete</button>
          <button type="button" className="btn-secondary btn-small" onClick={() => setPreview({ answers: {}, attachments: {}, errors: null })}>Clear</button>
        </div>
      </div>
      <p className="fd-hint">Fill it in as an engineer would. Nothing is saved.</p>
      {preview.errors && (
        preview.errors.length === 0
          ? <p className="fd-check-ok" role="status">Nothing missing: this would complete.</p>
          : (
            <div className="fd-check-list" role="status">
              <strong>Still needed before it completes:</strong>
              <ul>{preview.errors.map(item => <li key={`${item.field_key}-${item.reason}`}>{item.message}</li>)}</ul>
            </div>
          )
      )}
      <div className="fd-phone">
        <TemplateCheckFormRenderer
          schema={previewSchema}
          answers={preview.answers}
          onAnswersChange={(answers) => setPreview(prev => ({ ...prev, answers, errors: prev.errors ? null : prev.errors }))}
          showValidationErrors={Boolean(preview.errors && preview.errors.length > 0)}
          validationErrors={preview.errors || []}
          stepAttachments={preview.attachments}
          onAddPhotos={addPreviewFiles}
          onRemovePhoto={(stepKey, index) => setPreview(prev => ({
            ...prev,
            attachments: { ...prev.attachments, [stepKey]: (prev.attachments[stepKey] || []).filter((_, i) => i !== index) }
          }))}
        />
      </div>
    </div>
  );

  // The wrapper is a size container: the columns follow the space the
  // designer has, not the screen (it sits inside other layouts).
  return (
    <div className="fd-container">
    <div className="form-designer">
      {renderOutline()}
      <div className="fd-editor" aria-label="Edit">
        <div className="fd-panel-head"><h4>{selectedQuestion ? 'Question' : selectedSection ? 'Section' : 'Edit'}</h4></div>
        {notice && <p className="fd-notice" role="status">{notice}</p>}
        {selectedQuestion && renderQuestionEditor(selectedQuestion)}
        {!selectedQuestion && selectedSection && renderSectionEditor(selectedSection)}
        {!selectedQuestion && !selectedSection && <p className="fd-empty">Pick a section or question in the outline to change it.</p>}
      </div>
      {renderPreview()}
    </div>
    </div>
  );
}

FormDesigner.propTypes = {
  schema: PropTypes.shape({ sections: PropTypes.array }).isRequired,
  onChange: PropTypes.func.isRequired,
  publishedKeys: PropTypes.shape({ sections: PropTypes.instanceOf(Set), fields: PropTypes.instanceOf(Set) })
};

export default FormDesigner;
